import { useQueryClient } from 'react-query';
import { UseBaseHooks } from './use-base.hooks';
import { useMemo } from 'react';
import { CacheOperation } from '@enums/cache-operation.enum';

// Used to perform add, update, delete in quertClient cache.
export const useReactQueryCaching = (
  queryKey: string,
  featureCheck: boolean = true,
) => {
  const queryClient = useQueryClient();
  const { hasTenantPreference } = UseBaseHooks();

  const isCacheDisabled = useMemo(() => {
    return featureCheck && !hasTenantPreference('enableFrontEndCaching');
  }, [featureCheck]);

  const cacheOperationHandler = (operation: CacheOperation, data?: any) => {
    if (isCacheDisabled) return;
    switch (operation) {
      case CacheOperation.PREPEND_CACHE_ITEM_IN_FIRST_PAGE:
        prependItemToCacheInFirstPage(data);
        break;
      case CacheOperation.APPEND_CACHE_ITEM_IN_LAST_PAGE:
        appendItemToCacheInLastPage(data);
        break;
      case CacheOperation.UPDATE_CACHE_ITEM:
        updateItemInCache(data);
        break;
      case CacheOperation.DELETE_CACHE_ITEM:
        deleteItemFromCache(data);
        break;
      case CacheOperation.REMOVE_CACHE:
        removeCache();
        break;
    }
  };

  const appendItemToCacheInLastPage = (newItem) => {
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      const lastPageIndex = oldData.pages.length - 1;
      return {
        ...oldData,
        pages: oldData.pages.map((page, index) => ({
          ...page,
          totalElements: page.totalElements + 1,
          content:
            index === lastPageIndex ? [...page.content, newItem] : page.content,
        })),
      };
    });
  };

  const prependItemToCacheInFirstPage = (newItem) => {
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page, index) => ({
          ...page,
          totalElements: page.totalElements + 1,
          content: index === 0 ? [newItem, ...page.content] : page.content,
        })),
      };
    });
  };

  const updateItemInCache = (updatedItem) => {
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;

      const updatedPages = oldData.pages.map((page) => {
        const updatedContent = page.content.map((item) => {
          return item.id === updatedItem.id
            ? { ...item, ...updatedItem }
            : item;
        });

        return { ...page, content: updatedContent };
      });

      return { ...oldData, pages: updatedPages };
    });
  };

  const deleteItemFromCache = (itemId) => {
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;

      const updatedPages = oldData.pages.map((page) => {
        const updatedContent = page.content.filter(
          (item) => item.id !== itemId,
        );
        const updatedTotalElements = page.totalElements - 1;
        return {
          ...page,
          totalElements: updatedTotalElements,
          content: updatedContent,
        };
      });

      return {
        ...oldData,
        pages: updatedPages,
      };
    });
  };

  const removeCache = () => {
    const queries = queryClient.getQueryCache().getAll();
    const keysToRemove = queries
      .filter((query) => {
        const queryKeys = query?.queryKey?.toString();
        return queryKeys?.startsWith(queryKey);
      })
      .map((query) => query.queryKey);
    keysToRemove.forEach((key) => queryClient.removeQueries(key));
  };

  // Pass 0 as argument to disable cache while feature is enabled.
  const getCachingTime = (timeInMinutes: number = 5) => {
    return isCacheDisabled ? 0 : 1000 * 60 * timeInMinutes;
  };

  return {
    cacheOperationHandler,
    getCachingTime,
  };
};

export const getNextPageParam = (lastPage) => {
  return lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1;
};
